/**
 * LinkedIn post generation utilities.
 * Supports both legacy single-brace {variable} and PDF-style {{variable}} placeholders.
 */
import { formatDate } from './dateFormatter';

const SITE_URL = process.env.REACT_APP_SITE_URL || window.location.origin;

export const stripHtml = (html) => {
  if (!html) return '';
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const formatPostDate = (d) => formatDate(d, { style: 'long', fallback: 'TBD' });

/**
 * Generate a LinkedIn post for a single RFQ using the individual template.
 * Supports legacy {var} and PDF-style {{var}} placeholders.
 */
export const generateIndividualPost = (template, rfq) => {
  const locations = (rfq.locations || []).join(', ') || 'Various';
  const clientName = rfq.client?.name || rfq.client_name || '';

  return template
    // Legacy single-brace variables
    .replace(/\{title\}/g, rfq.title || '')
    .replace(/\{description\}/g, stripHtml(rfq.description) || '')
    .replace(/\{locations\}/g, locations)
    .replace(/\{clearance_required\}/g, rfq.clearance_required || 'None')
    .replace(/\{work_arrangement\}/g, rfq.work_arrangement || 'TBD')
    .replace(/\{estimated_start_date\}/g, formatPostDate(rfq.estimated_start_date))
    .replace(/\{application_url\}/g, `${SITE_URL}/job/${rfq.id}/apply`)
    // PDF-style double-brace variables
    .replace(/\{\{job_title\}\}/g, rfq.title || '')
    .replace(/\{\{ref_code\}\}/g, rfq.ref_code || '')
    .replace(/\{\{client_name\}\}/g, clientName)
    .replace(/\{\{clearance\}\}/g, rfq.clearance_required || 'None')
    .replace(/\{\{locations\}\}/g, locations)
    .replace(/\{\{work_arrangement\}\}/g, rfq.work_arrangement || 'TBD')
    .replace(/\{\{close_date\}\}/g, formatPostDate(rfq.client_close_datetime))
    .replace(/\{\{start_date\}\}/g, formatPostDate(rfq.estimated_start_date))
    .replace(/\{\{term\}\}/g, rfq.initial_term_months ? `${rfq.initial_term_months} months` : 'TBD')
    .replace(/\{\{extension\}\}/g, rfq.extension_options || 'N/A')
    .replace(/\{\{description\}\}/g, stripHtml(rfq.description) || '')
    .replace(/\{\{criteria\}\}/g, stripHtml(rfq.selection_criteria) || '')
    .replace(/\{\{application_url\}\}/g, `${SITE_URL}/job/${rfq.id}/apply`);
};

/**
 * Generate a LinkedIn post for multiple RFQs using the multi template.
 * Supports {title.1}, {description.1} (legacy) and {{job_title.1}}, {{description.1}} etc.
 * Unused numbered placeholders are removed.
 */
export const generateMultiPost = (template, rfqs) => {
  let content = template;

  rfqs.forEach((rfq, index) => {
    const n = index + 1;
    const locations = (rfq.locations || []).join(', ') || 'Various';
    const clientName = rfq.client?.name || rfq.client_name || '';

    // Legacy single-brace numbered variables
    content = content
      .replace(new RegExp(`\\{title\\.${n}\\}`, 'g'), rfq.title || '')
      .replace(new RegExp(`\\{description\\.${n}\\}`, 'g'), stripHtml(rfq.description) || '');

    // PDF-style double-brace numbered variables
    content = content
      .replace(new RegExp(`\\{\\{job_title\\.${n}\\}\\}`, 'g'), rfq.title || '')
      .replace(new RegExp(`\\{\\{ref_code\\.${n}\\}\\}`, 'g'), rfq.ref_code || '')
      .replace(new RegExp(`\\{\\{client_name\\.${n}\\}\\}`, 'g'), clientName)
      .replace(new RegExp(`\\{\\{clearance\\.${n}\\}\\}`, 'g'), rfq.clearance_required || 'None')
      .replace(new RegExp(`\\{\\{locations\\.${n}\\}\\}`, 'g'), locations)
      .replace(new RegExp(`\\{\\{work_arrangement\\.${n}\\}\\}`, 'g'), rfq.work_arrangement || 'TBD')
      .replace(new RegExp(`\\{\\{close_date\\.${n}\\}\\}`, 'g'), formatPostDate(rfq.client_close_datetime))
      .replace(new RegExp(`\\{\\{start_date\\.${n}\\}\\}`, 'g'), formatPostDate(rfq.estimated_start_date))
      .replace(new RegExp(`\\{\\{term\\.${n}\\}\\}`, 'g'), rfq.initial_term_months ? `${rfq.initial_term_months} months` : 'TBD')
      .replace(new RegExp(`\\{\\{extension\\.${n}\\}\\}`, 'g'), rfq.extension_options || 'N/A')
      .replace(new RegExp(`\\{\\{description\\.${n}\\}\\}`, 'g'), stripHtml(rfq.description) || '')
      .replace(new RegExp(`\\{\\{criteria\\.${n}\\}\\}`, 'g'), stripHtml(rfq.selection_criteria) || '');
  });

  // Clean up any unused numbered placeholders
  content = content
    .replace(/\{title\.\d+\}/g, '')
    .replace(/\{description\.\d+\}/g, '')
    .replace(/\{\{[\w_]+\.\d+\}\}/g, '');

  return content;
};
